/**
 * Repair Standard MIDI File track-chunk lengths.
 *
 * Some exporters write MTrk length fields that are a few bytes off. Strict
 * parsers (`midi-file` / `@tonejs/midi`) then land mid-header and throw
 * `Expected 'MTrk', got: 'Trk…'`. Resync each chunk to the next MTrk / EOF.
 */

function readU16(bytes: Uint8Array, offset: number): number {
  return ((bytes[offset] ?? 0) << 8) | (bytes[offset + 1] ?? 0);
}

function readU32(bytes: Uint8Array, offset: number): number {
  return (
    ((bytes[offset] ?? 0) << 24)
    | ((bytes[offset + 1] ?? 0) << 16)
    | ((bytes[offset + 2] ?? 0) << 8)
    | (bytes[offset + 3] ?? 0)
  ) >>> 0;
}

function writeU32(bytes: Uint8Array, offset: number, value: number): void {
  bytes[offset] = (value >>> 24) & 0xff;
  bytes[offset + 1] = (value >>> 16) & 0xff;
  bytes[offset + 2] = (value >>> 8) & 0xff;
  bytes[offset + 3] = value & 0xff;
}

function isMTrk(bytes: Uint8Array, offset: number): boolean {
  return (
    offset + 4 <= bytes.length
    && bytes[offset] === 0x4d
    && bytes[offset + 1] === 0x54
    && bytes[offset + 2] === 0x72
    && bytes[offset + 3] === 0x6b
  );
}

function isMThd(bytes: Uint8Array, offset: number): boolean {
  return (
    offset + 4 <= bytes.length
    && bytes[offset] === 0x4d
    && bytes[offset + 1] === 0x54
    && bytes[offset + 2] === 0x68
    && bytes[offset + 3] === 0x64
  );
}

/** Search near a declared chunk end for the real next `MTrk` (or EOF). */
function locateTrackEnd(
  bytes: Uint8Array,
  dataStart: number,
  declaredEnd: number,
  remainingTracks: number,
): number {
  const fileEnd = bytes.length;
  if (remainingTracks <= 0) {
    return fileEnd;
  }
  const tryAt = (offset: number): number | undefined => {
    if (offset === fileEnd) {
      return offset;
    }
    if (offset >= dataStart && offset + 4 <= fileEnd && isMTrk(bytes, offset)) {
      return offset;
    }
    return undefined;
  };

  const exact = tryAt(declaredEnd);
  if (exact !== undefined) {
    return exact;
  }

  // Common exporter bug: length is a handful of bytes too long/short.
  for (let delta = 1; delta <= 64; delta++) {
    const back = tryAt(declaredEnd - delta);
    if (back !== undefined) {
      return back;
    }
    const forward = tryAt(declaredEnd + delta);
    if (forward !== undefined) {
      return forward;
    }
  }

  // Last resort: scan forward from the track data for the next MTrk.
  for (let j = dataStart; j + 4 <= fileEnd; j++) {
    if (isMTrk(bytes, j)) {
      return j;
    }
  }
  return fileEnd;
}

export type RepairSmfResult = {
  bytes: Uint8Array;
  repaired: boolean;
  warnings: string[];
};

/**
 * Return SMF bytes with corrected MTrk length fields when needed.
 * Pass-through for already-valid files (same buffer contents; may copy).
 */
export function repairSmfTrackChunkLengths(input: Uint8Array): RepairSmfResult {
  const warnings: string[] = [];
  if (input.length < 14 || !isMThd(input, 0)) {
    return { bytes: input, repaired: false, warnings };
  }

  const headerDataLen = readU32(input, 4);
  if (headerDataLen < 6 || 8 + headerDataLen > input.length) {
    return { bytes: input, repaired: false, warnings };
  }

  const trackCount = readU16(input, 10);
  const bytes = input.slice();
  let repaired = false;
  let pos = 8 + headerDataLen;

  for (let track = 0; track < trackCount; track++) {
    // Resync if a previous bad length left us off a boundary.
    if (!isMTrk(bytes, pos)) {
      let found = -1;
      for (let j = pos; j + 8 <= bytes.length; j++) {
        if (isMTrk(bytes, j)) {
          found = j;
          break;
        }
      }
      if (found < 0) {
        warnings.push(`Track ${track + 1}: missing MTrk chunk (expected ${trackCount} tracks).`);
        break;
      }
      if (found !== pos) {
        warnings.push(`Track ${track + 1}: resynced chunk header (+${found - pos} bytes).`);
      }
      pos = found;
    }

    const declared = readU32(bytes, pos + 4);
    const dataStart = pos + 8;
    const declaredEnd = dataStart + declared;
    const remainingAfter = trackCount - track - 1;
    const actualEnd = locateTrackEnd(bytes, dataStart, declaredEnd, remainingAfter);
    const correctLen = Math.max(0, actualEnd - dataStart);

    if (correctLen !== declared) {
      writeU32(bytes, pos + 4, correctLen);
      repaired = true;
      warnings.push(
        `Track ${track + 1}: fixed chunk length ${declared} → ${correctLen}.`,
      );
    }
    pos = actualEnd;
  }

  return { bytes, repaired, warnings };
}
